import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Landmark } from 'lucide-react';
import { useConfirm } from '../../components/ConfirmDialog';
import { DetailDrawer, ErrorState, money, fmtDate } from './design';

// Pay approved supplier invoices in one batch, the way Brevity pays brokered suppliers: a bank
// (ABA) file to upload to the bank, the invoices marked paid, and a remittance email to each
// supplier. An invoice whose provider has no bank details cannot be included until they are added.

function saveText(filename, text) {
  const blob = new Blob([text], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

export default function PaySuppliers({ open, onClose, onDone }) {
  const confirm = useConfirm();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState({});
  const [loading, setLoading] = useState(false);
  // A failed load is said as a failure: before, it fell through to "No approved invoices", which
  // reads as "nothing to pay" when the truth is "we could not look".
  const [error, setError] = useState(null);
  const [paying, setPaying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get('/aged-care/supplier-invoices/payable');
      setData(r.data);
      const p = {};
      (r.data.invoices || []).forEach((i) => { if (i.hasBank) p[i.id] = true; });
      setPicked(p);
    } catch (e) {
      setData(null);
      setError(e.response?.data?.message || 'Could not load the approved invoices');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const invoices = (data && data.invoices) || [];
  const chosen = invoices.filter((i) => picked[i.id]);
  const total = chosen.reduce((n, i) => n + i.total, 0);

  const pay = async () => {
    // Paying is not undone from here: the bank file is made, the invoices are marked paid and the
    // remittances go out, so it says exactly what is about to happen first.
    const n = chosen.length;
    const ok = await confirm(
      `This makes a bank file paying ${n} invoice${n === 1 ? '' : 's'} totalling ${money(total)}, marks ${n === 1 ? 'it' : 'them'} paid, and emails a remittance to each supplier with an email address. Upload the file to your bank to send the money.`,
      { title: `Pay ${n} invoice${n === 1 ? '' : 's'} for ${money(total)}?`, confirmLabel: `Pay ${money(total)}` },
    );
    if (!ok) return;
    setPaying(true);
    try {
      const r = await api.post('/aged-care/supplier-invoices/pay', { ids: chosen.map((i) => i.id) });
      saveText(r.data.filename, r.data.aba);
      toast.success(`${r.data.paid} invoice${r.data.paid === 1 ? '' : 's'} paid, ${money(r.data.total)}. Upload ${r.data.filename} to your bank.${r.data.emailed ? ` ${r.data.emailed} remittance email${r.data.emailed === 1 ? '' : 's'} sent.` : ''}`, { duration: 8000 });
      onDone?.(); onClose?.();
    } catch (e) { toast.error(e.response?.data?.message || 'Could not create the payment file'); }
    finally { setPaying(false); }
  };

  return (
    <DetailDrawer
      open={open} onClose={onClose} width="lg" icon={Landmark}
      title="Pay approved invoices"
      subtitle="Creates a bank file to upload to your bank, and emails each supplier a remittance."
      footer={<>
        <button type="button" onClick={onClose} className="btn-secondary text-sm">Cancel</button>
        <button type="button" onClick={pay} disabled={paying || loading || !!error || !chosen.length || (data && !data.bankReady)} className="btn-primary text-sm">
          {paying ? 'Creating…' : `Pay ${chosen.length} for ${money(total)}`}
        </button>
      </>}
    >
      {loading ? (
        <p className="text-[13px] text-slate-500 py-6 text-center">Loading approved invoices…</p>
      ) : error ? (
        <ErrorState message={error} onRetry={load} />
      ) : (
        <div className="space-y-4">
          {data && !data.bankReady && (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-[13px] text-amber-900">
              Your bank's Direct Entry details are not set. Add them in Finance, Pay contractors, Payment settings.
            </p>
          )}
          {!invoices.length ? (
            <p className="text-[13px] text-slate-500 py-6 text-center">No approved invoices are waiting to be paid.</p>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-[13px]">
                <thead className="bg-slate-50 text-slate-600 text-left">
                  <tr>
                    <th className="px-3 py-2 w-8"><span className="sr-only">Pay</span></th>
                    <th className="px-3 py-2 font-medium">Supplier and invoice</th>
                    <th className="px-3 py-2 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {invoices.map((i) => (
                    <tr key={i.id} className={i.hasBank ? '' : 'bg-amber-50/50'}>
                      <td className="px-3 py-2 align-top">
                        <input type="checkbox" disabled={!i.hasBank} checked={!!picked[i.id]} aria-label={`Pay invoice ${i.invoiceNumber}`}
                          onChange={(e) => setPicked((p) => ({ ...p, [i.id]: e.target.checked }))} />
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-900">{i.supplier}</div>
                        <div className="text-[12px] text-slate-500">Invoice {i.invoiceNumber}{i.date ? `, ${fmtDate(i.date)}` : ''}</div>
                        {/* A reimbursement is paid into the participant's own account, not the supplier's. */}
                        {!i.hasBank && (i.reimbursement
                          ? <div className="text-[12px] text-amber-800 mt-0.5">No bank account for the participant. Add it on their funding plan (Self-management), and choose the participant on the invoice.</div>
                          : <div className="text-[12px] text-amber-800 mt-0.5">No bank details. Add them in Providers and purchase orders.</div>)}
                        {i.hasBank && !i.remitTo && <div className="text-[12px] text-slate-500 mt-0.5">No email for a remittance.</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(i.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </DetailDrawer>
  );
}
